import { AnimatedModal } from "@/src/components/AnimatedModal";
import { CloseIcon } from "@/src/components/CloseIcon";
import { H4 } from "@/src/components/Heading";
import { Spinner } from "@/src/components/icons/Spinner";
import Loading from "@/src/components/Loading";
import { Button } from "@/src/components/tailwind/Button";
import {
  DealExportsQuery,
  DealExportStatus,
  useDealExportsQuery,
} from "@/src/graphql/generated";
import useGqlClient from "@/src/hooks/useGqlClient";
import { format, formatDistanceToNowStrict, fromUnixTime } from "date-fns";
import { CircleCheckIcon, DownloadIcon, XCircleIcon } from "lucide-react";
import { useState } from "react";
import { useParams } from "react-router-dom";

interface DownloadsModalProps {
  open: boolean;
  onClose: () => void;
}

export function DownloadsModal(props: DownloadsModalProps) {
  return (
    <AnimatedModal open={props.open} onClose={props.onClose} size="xl">
      <div>
        <div className="flex items-center justify-between">
          <H4>Exports</H4>
          <CloseIcon onClose={props.onClose} />
        </div>
        <DownloadsModalContent />
      </div>
    </AnimatedModal>
  );
}

function DownloadsModalContent() {
  const client = useGqlClient();
  const { dealId: activeDealId } = useParams<{ dealId: string }>();

  const dealExportsQuery = useDealExportsQuery(client, {
    dealId: activeDealId ?? "",
  });

  if (dealExportsQuery.error) {
    return (
      <div className="flex flex-1 w-full h-full justify-center items-center">
        <p>Error loading bulk exports</p>
      </div>
    );
  }

  if (!dealExportsQuery.data || dealExportsQuery.isPending) {
    return <Loading />;
  }

  return (
    <div className="mt-4">
      <div className="w-full h-96 overflow-y-scroll border border-gray-200 rounded-md p-4">
        {dealExportsQuery.data.deal.dealExports.length === 0 ? (
          <p className="text-sm text-gray-500 text-center">No exports</p>
        ) : null}
        {dealExportsQuery.data.deal.dealExports.map((dealExport) => (
          <DealExport dealExport={dealExport} key={dealExport.id} />
        ))}
      </div>
    </div>
  );
}

function DealExport(props: {
  dealExport: DealExportsQuery["deal"]["dealExports"][0];
}) {
  const { dealExport } = props;
  const [isDownloading, setIsDownloading] = useState(false);

  return (
    <div key={dealExport.id} className="flex items-center justify-between py-2">
      <div className="flex items-center gap-x-4">
        <DealExportStatusBadge status={dealExport.status} />
        <div>
          <p className="text-sm">
            {dealExport.name
              ? dealExport.name
              : format(fromUnixTime(dealExport.createdAt), "dd/MM/yyyy")}
          </p>
          <p className="text-xs text-gray-500">
            Created by {dealExport.createdBy.email}
            {" \u2022 "}
            {formatDistanceToNowStrict(fromUnixTime(dealExport.createdAt), {
              addSuffix: true,
            })}
          </p>
        </div>
      </div>
      {dealExport.status === DealExportStatus.Complete ? (
        <div className="flex items-center gap-x-2">
          <Button
            isDisabled={!dealExport.s3PresignedUrl || isDownloading}
            variant="neutral"
            loadingText="Downloading..."
            isLoading={isDownloading}
            onClick={() => {
              if (!dealExport.s3PresignedUrl || isDownloading) {
                return;
              }

              setIsDownloading(true);

              const a = document.createElement("a");
              a.href = dealExport.s3PresignedUrl;
              a.target = "_blank";

              document.body.appendChild(a);
              a.click();

              document.body.removeChild(a);

              setTimeout(() => {
                setIsDownloading(false);
              }, 1000);
            }}
            text="Download .zip"
            icon={DownloadIcon}
            size="s"
          />
        </div>
      ) : dealExport.status === DealExportStatus.Failed ? (
        <p className="text-red-700 text-xs font-semibold">Failed</p>
      ) : (
        <div className="flex items-center -space-x-1.5">
          <p className="text-gray-500 text-xs font-semibold">Generating .zip</p>
        </div>
      )}
    </div>
  );
}

function DealExportStatusBadge(props: { status: DealExportStatus }) {
  if (props.status === DealExportStatus.Complete) {
    return <CircleCheckIcon className="h-4 w-4 text-green-500" />;
  }

  if (props.status === DealExportStatus.Failed) {
    return <XCircleIcon className="h-4 w-4 text-red-700" />;
  }

  return <Spinner color="gray" size="s" withMargin={false} />;
}
